import { useState, useCallback, useRef, useEffect } from 'react';

// Maximum number of API calls to keep in history
const MAX_HISTORY_SIZE = 100;

// Global state for API debug history (shared across all useApiDebug instances)
let globalHistory = [];
let globalListeners = [];

/**
 * Custom hook for managing API debugging history
 * Provides centralized state management for API call logging
 */
export const useApiDebug = () => {
  const [history, setHistory] = useState(globalHistory);
  const [isOpen, setIsOpen] = useState(false);
  const listenerRef = useRef(null);

  // Subscribe to global history updates
  useEffect(() => {
    const listener = (newHistory) => {
      setHistory([...newHistory]);
    };
    listenerRef.current = listener;
    globalListeners.push(listener);

    // Cleanup on unmount
    return () => {
      globalListeners = globalListeners.filter(l => l !== listenerRef.current);
    };
  }, []);

  /**
   * Add a new API call to history
   * @param {Object} apiCall - API call details
   */
  const addApiCall = useCallback((apiCall) => {
    const callWithId = {
      ...apiCall,
      id: Date.now() + Math.random().toString(36).substr(2, 9),
      timestamp: new Date().toISOString(),
    };

    // Add to beginning of array (most recent first)
    globalHistory = [callWithId, ...globalHistory];

    // Prune old entries if exceeds max size
    if (globalHistory.length > MAX_HISTORY_SIZE) {
      globalHistory = globalHistory.slice(0, MAX_HISTORY_SIZE);
    }

    // Notify all listeners
    globalListeners.forEach(listener => listener(globalHistory));

    // Persist to sessionStorage
    try {
      sessionStorage.setItem('apiDebugHistory', JSON.stringify(globalHistory.slice(0, 50)));
    } catch (error) {
      console.warn('Failed to persist API debug history:', error);
    }
  }, []);

  /**
   * Clear all API call history
   */
  const clearHistory = useCallback(() => {
    globalHistory = [];
    globalListeners.forEach(listener => listener(globalHistory));
    try {
      sessionStorage.removeItem('apiDebugHistory');
    } catch (error) {
      console.warn('Failed to clear API debug history:', error);
    }
  }, []);

  /**
   * Toggle panel open/close
   */
  const togglePanel = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  /**
   * Open panel
   */
  const openPanel = useCallback(() => {
    setIsOpen(true);
  }, []);

  /**
   * Close panel
   */
  const closePanel = useCallback(() => {
    setIsOpen(false);
  }, []);

  return {
    history,
    isOpen,
    addApiCall,
    clearHistory,
    togglePanel,
    openPanel,
    closePanel,
  };
};

/**
 * Load persisted history from sessionStorage on initial load
 */
export const loadPersistedHistory = () => {
  try {
    const persisted = sessionStorage.getItem('apiDebugHistory');
    if (persisted) {
      const parsed = JSON.parse(persisted);
      if (Array.isArray(parsed)) {
        globalHistory = parsed;
        return parsed;
      }
    }
  } catch (error) {
    console.warn('Failed to load persisted API debug history:', error);
  }
  return [];
};

/**
 * Get reference to addApiCall function without needing the hook
 * Used by api.js interceptors
 */
export const getApiDebugLogger = () => {
  return (apiCall) => {
    const callWithId = {
      ...apiCall,
      id: Date.now() + Math.random().toString(36).substr(2, 9),
      timestamp: new Date().toISOString(),
    };

    globalHistory = [callWithId, ...globalHistory];

    if (globalHistory.length > MAX_HISTORY_SIZE) {
      globalHistory = globalHistory.slice(0, MAX_HISTORY_SIZE);
    }

    globalListeners.forEach(listener => listener(globalHistory));

    try {
      sessionStorage.setItem('apiDebugHistory', JSON.stringify(globalHistory.slice(0, 50)));
    } catch (error) {
      // Silent fail
    }
  };
};

// Load persisted history on module load
loadPersistedHistory();
